import React from 'react';
import { useHistory, useLocation, Redirect } from 'react-router-dom';
import Firebase from 'firebase/app';
import { useCollection } from 'react-firebase-hooks/firestore';
import { IconButton } from '@material-ui/core';
import { toaster } from 'evergreen-ui';

import { useSession } from '../../hooks';
import { NavLink, Container, Loader } from '../../styleguide';
import { Page } from '../common';
import { createShop } from '../../actions/shop';
import { createExpressAccount } from '../../actions/stripe';
import { ImageStyled } from '../../styles/signup';
import { CloseIcon } from '../../styleguide/icons';
import STEPS from './steps';
import { useTheme } from '../../hooks';
import FoodImage from '../../assets/images/food.svg';

const SHOP_DATA = {
  name: '',
  description: '',
  ownerID: '',
  available: false,
  category: '',
  legal: null,
  address: null,
  logoURL: '',
};

const CreateShop = props => {
  const { authUser, user } = useSession();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();

  const [currentStep, setStep] = React.useState(1);
  const [creationError, setError] = React.useState(null);
  const [isCreatingAccount, setIsCreatingAccount] = React.useState(false);
  const [shop, setShop] = React.useState({
    ...SHOP_DATA,
    ownerID: authUser.uid,
  });

  const imageSize =
    currentStep === 1 ? {} : { width: '150px', height: '150px' };

  const onSubmit = async () => {
    if (!isCreatingAccount) {
      let shopId = null;
      try {
        setIsCreatingAccount(true);

        // Create shop in Firestore
        shopId = await createShop(shop);

        // Create Stripe Express account
        const shopData = {
          name: shop.name,
          ownerEmail: user.email,
          url: `${process.env.REACT_APP_FIREBASE_WEBAPP_URL}/shop/${shopId}`,
        };

        const result = await createExpressAccount(shopId, shopData);

        if (result.success && result.accountLinkUrl) {
          toaster.success('Etablissement créé avec succès !');
          history.push(`/shopDashboard/${shopId}`);
          setIsCreatingAccount(false);
        } else {
          throw new Error('Failed to create Express account');
        }
      } catch (error) {
        // Rollback: delete shop if Stripe account creation failed
        if (shopId) {
          try {
            await Firebase.firestore()
              .collection('shops')
              .doc(shopId)
              .delete();
          } catch (rollbackError) {
            // Log rollback failure but don't override the original error
          }
        }
        setError(error);
        setIsCreatingAccount(false);
      }
    }
  };

  const onShopUpdate = data => {
    setShop({ ...shop, ...data });
  };

  const onNextStep = () => setStep(currentStep + 1);

  const onSkip = () => setStep(4);

  React.useEffect(() => {
    const isFinalStep =
      !!shop.name &&
      !!shop.category &&
      !!shop.legal &&
      !!shop.address &&
      !!shop.logoURL;

    if (isFinalStep) {
      onSubmit();
    }
  });

  const [value, loading, error] = useCollection(
    Firebase.firestore()
      .collection('shops')
      .where('ownerID', '==', user.id),
    {
      snapshotListenOptions: { includeMetadataChanges: true },
    }
  );

  if (loading) {
    return (
      <Loader color={theme.primaryColor} background={theme.backgroundColor} />
    );
  }

  const alreadyHasAShop = !value.empty;

  if (alreadyHasAShop) {
    const shopData = value.docs[0].data();
    const shopId = value.docs[0].id;

    // Legacy OAuth system: stripeCustomerId means fully onboarded
    if (shopData.stripeCustomerId) {
      return (
        <Redirect
          to={{
            pathname: `/shopDashboard/${shopId}`,
            state: { from: location },
          }}
        />
      );
    }

    // New Express system: check if onboarding is complete
    if (shopData.stripeAccountId && shopData.stripeChargesEnabled) {
      return (
        <Redirect
          to={{
            pathname: `/shopDashboard/${shopId}`,
            state: { from: location },
          }}
        />
      );
    }

    if (shopData.stripeAccountId && !shopData.stripeChargesEnabled) {
      // Express account exists but onboarding incomplete
      return (
        <Redirect
          to={{
            pathname: '/shop/stripe/onboarding',
            search: `?shop=${shopId}`,
            state: { from: location },
          }}
        />
      );
    }

    // No Stripe account at all, start Express onboarding
    return (
      <Redirect
        to={{
          pathname: '/shop/stripe/onboarding',
          search: `?shop=${shopId}`,
          state: { from: location },
        }}
      />
    );
  }

  if (creationError) {
    toaster.danger("Erreur lors de la création de l'établissement", {
      description: creationError.message,
    });
  }

  const { Step } = STEPS.filter(item => item.index === currentStep)[0];

  if (isCreatingAccount) {
    return (
      <Page name="Création du compte Stripe">
        <Container>
          <Loader
            color={theme.primaryColor}
            background={theme.backgroundColor}
          />
          <div style={{ textAlign: 'center', marginTop: '20px' }}>
            <p>Création de votre compte Stripe en cours...</p>
          </div>
        </Container>
      </Page>
    );
  }

  return (
    <Page
      navbarLeftChild="disabled"
      navbarRightChild={
        <NavLink color={theme.headingColor} to={{ pathname: '/' }}>
          <IconButton aria-label="retour">
            <CloseIcon color={theme.headingColor} size="16px" />
          </IconButton>
        </NavLink>
      }
      name="Nouvel établissement"
    >
      <Container>
        <ImageStyled src={FoodImage} {...imageSize} />

        <Step
          shop={shop}
          onNext={onNextStep}
          onSkip={onSkip}
          onShopUpdate={onShopUpdate}
        />
      </Container>
    </Page>
  );
};

export default CreateShop;
